import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:bg-gray disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'bg-btn-gradient text-[#072C50] font-semibold hover:opacity-90 shadow-gold-shadow',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-[#EAAD07]/50 bg-background text-[#072C50] hover:bg-[#EAAD07]/10 hover:border-[#EAAD07]',
        secondary: 'bg-[#072C50] text-white hover:bg-[#072C50]/90',
        ghost: 'text-[#072C50] hover:bg-[#072C50]/5 hover:text-[#072C50]',
        link: 'text-[#072C50] underline-offset-4 hover:underline',
        green: 'border-green bg-green2 text-black',
        yellow: 'bg-btn-gradient rounded-xl text-[#072C50] font-semibold',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xsm: 'h-7 rounded-md w-7',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        xl: 'h-[52px] rounded-xl',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
